<!--
 * @Author: Tahiti
 * @Date: 2025-11-19 14:14:14
 * @LastEditors: Tahiti
 * @LastEditTime: 2026-01-14
 * @Description: 
 * @FilePath: /weryai-web/pages/video-effects/index.vue
-->
<template>
    <div class="video-effects-page">
        <!-- 顶部轮播 -->
        <div class="mb-10">
            <ClientOnly>
                <EffectsCarousel :items="carouselItems" type="VIDEO" @use-template="handleUseTemplate"
                    @item-click="handleUseTemplate" />
                <template #fallback>
                    <div class="flex gap-5 overflow-hidden">
                        <div v-for="i in 3" :key="i" class="flex-shrink-0">
                            <div class="w-[632px] h-[316px] rounded-xl bg-[#1b1d25] animate-pulse"></div>
                            <div class="h-5 w-48 bg-[#1b1d25] rounded mt-5"></div>
                        </div>
                    </div>
                </template>
            </ClientOnly>
        </div>

        <!-- 标题 -->
        <h1 class="section-title transition-all duration-300" :style="{
            marginLeft:
                isClient && layoutInfo.offsetX > 0
                    ? `${layoutInfo.offsetX}px`
                    : '0px',
        }">
            {{ t('effects.aiVideoEffectsTitle') }}
        </h1>
        <!-- 吸顶 Tab 列表（暂时隐藏，数据多了再启用）
        <ClientOnly>
            <EffectsCategoryTabs 
                v-model="activeTab" 
                :tabs="categoryTabs"
                :is-sticky="true"
                :is-visible="showStickyTabs"
                @change="handleTabChange"
            />
        </ClientOnly>
        -->

        <!-- 分类内容区域（暂时隐藏，数据多了再启用）
        <div class="category-sections">
            <EffectsCategorySection
                v-for="category in visibleCategories"
                :key="category.id"
                :title="category.name"
                :icon="category.icon"
                :category-id="category.id"
                :items="category.items"
                :base-path="'/video-effects/category'"
                @card-click="handleCardClick"
                @use-template="handleUseTemplate"
            />
        </div>
        -->

        <!-- 视频特效网格区域（简化版，直接展示所有视频模板） -->
        <div ref="containerRef" class="video-effects-grid" :class="{ 'is-ready': isLayoutReady }"
            :style="{ height: `${containerHeight}px` }">
            <template v-if="isLayoutReady">
                <div v-for="item in gridItems" :key="item.id" class="grid-item absolute transition-all duration-300"
                    :style="item.style">
                    <EffectsCard :item="item" :width="columnWidth" :height="item.calculatedHeight"
                        @click="handleCardClick" @use-template="handleUseTemplate" @media-load="onMediaLoad" />
                </div>
            </template>
        </div>

        <!-- 加载更多触发器（IntersectionObserver 监听） -->
        <div ref="loadMoreSentinel" class="load-more-sentinel h-1"></div>

        <!-- 加载更多 -->
        <div v-if="loading" class="loading-indicator flex justify-center py-8">
            <l-dot-spinner v-if="isClient" size="30" speed="0.9" color="var(--el-menu-active-color)"></l-dot-spinner>
        </div>

        <!-- 没有更多数据 -->
        <div v-if="!hasMore && items.length > 0" class="no-more-data flex justify-center py-6 text-gray-400 text-sm">
            {{ t('inspiration.noMoreData') }}
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { definePageMeta, navigateTo, useAsyncData, useRoute } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { analyticsEvent } from '~/utils/log-event-func'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import {
    getEffectTemplateTagListApi,
    getEffectTemplatePageApi,
    getEffectTemplateTopApi,
    getEffectTemplateLastUpdateApi,
} from '~/api/effects'
import { storage } from '~/utils/localstorage'
import EffectsCarousel from '~/components/Effects/EffectsCarousel.vue'
import EffectsCard from '~/components/Effects/EffectsCard.vue'
// 暂时注释掉，数据多了再启用
// import EffectsCategoryTabs from '~/components/Effects/EffectsCategoryTabs.vue'
// import EffectsCategorySection from '~/components/Effects/EffectsCategorySection.vue'

useMultilingualSeo({ pageType: 'videoEffects' })

definePageMeta({
    layout: 'laboratory',
})

const { t, locale } = useI18n()
const route = useRoute()

/**
 * 补齐“当前 URL 语言模式”的基础前缀
 * - /zh/... => /zh
 * - /en/... => /en（英文前缀模式）
 * - 英文无前缀 => ''
 *
 * 说明：这里优先读 address bar 的 pathname，避免 /en alias 场景下 route.path 丢前缀。
 */
const getUrlBasePrefix = () => {
    const supported = [
        'zh',
        'en',
        'ja',
        'ko',
        'es',
        'fr',
        'de',
        'ru',
        'pt',
        'it',
        'ar',
    ]
    const p =
        import.meta.client && typeof window !== 'undefined'
            ? window.location.pathname
            : route.path || '/'
    const m = p.match(/^\/([a-z]{2})(\/|$)/)
    const maybe = m?.[1]
    if (maybe && supported.includes(maybe)) {
        return maybe === 'en' ? '/en' : `/${maybe}`
    }
    return ''
}

const withUrlPrefix = toPath => {
    const base = getUrlBasePrefix()
    if (!base) return toPath
    if (toPath === '/') return base
    return `${base}${toPath}`
}

// 缓存配置（使用后端时间戳判断是否需要刷新缓存）
const CACHE_KEYS = {
    VIDEO_LIST: (lang, page) => `effects_video_list_${lang}_${page}`,
    VIDEO_TIMESTAMP: lang => `effects_video_timestamp_${lang}`, // 存储后端数据最后更新时间戳
}

// 网格布局配置
const config = {
    baseColumnWidth: 240,
    maxColumnWidth: 300,
    minColumnWidth: 140, // 下调最小宽度，确保移动端两列能放下
    gap: 16,
    minColumns: 2,
    defaultAspectRatio: 3 / 4, // 默认宽高比（宽/高），视觉更平衡
}

const containerRef = ref(null)
const containerWidth = ref(0)
const columnCount = ref(4)
const columnWidth = ref(config.baseColumnWidth)
const loading = ref(false)
const isLayoutReady = ref(false)
const isClient = ref(false)

// 分页相关
const currentPage = ref(1)
const pageSize = 12
const hasMore = ref(true)

// 预加载图片并获取尺寸（带 2 秒超时）
const preloadImage = (url, timeout = 2000) => {
    return new Promise(resolve => {
        if (!url) {
            resolve(null)
            return
        }

        const img = new Image()
        let resolved = false

        // 超时处理：超过指定时间返回 null
        const timer = setTimeout(() => {
            if (!resolved) {
                resolved = true
                resolve(null)
            }
        }, timeout)

        img.onload = () => {
            if (!resolved) {
                resolved = true
                clearTimeout(timer)
                resolve({ width: img.naturalWidth, height: img.naturalHeight })
            }
        }

        img.onerror = () => {
            if (!resolved) {
                resolved = true
                clearTimeout(timer)
                resolve(null)
            }
        }

        img.src = url
    })
}

// 解析后端返回的宽高比字符串（如 "9:16"）
const parseAspectRatio = aspectRatioText => {
    if (!aspectRatioText) return null
    const parts = aspectRatioText.split(':')
    const width = Number(parts[0])
    const height = Number(parts[1])
    if (!isNaN(width) && !isNaN(height) && width > 0 && height > 0) {
        return width / height
    }
    return null
}

// 获取宽高比（直接使用预加载的数据）
const getActualAspectRatio = item => {
    // 优先使用预加载获取的宽高比
    if (item.aspectRatio && item.aspectRatio > 0) {
        return item.aspectRatio
    }

    // 兼容后端返回的 aspect_ratio 字符串格式（如 "9:16"）
    const parsedRatio = parseAspectRatio(item.aspect_ratio)
    if (parsedRatio) return parsedRatio

    return config.defaultAspectRatio
}

// 根据宽高比计算卡片高度
const calculateItemHeight = item => {
    const aspectRatio = getActualAspectRatio(item)
    return Math.ceil(columnWidth.value / aspectRatio)
}

// 媒体加载完成（预加载方案下不再需要重新布局）
const onMediaLoad = (item, actualWidth, actualHeight) => {
    // 预加载已经获取了正确的宽高比，这里仅作为兜底
    // 如果预加载失败但媒体加载成功，可以更新宽高比
    if (actualWidth > 0 && actualHeight > 0 && !item.aspectRatio) {
        item.aspectRatio = actualWidth / actualHeight
    }
}

// 计算列数和列宽
const calculateColumns = () => {
    if (!containerRef.value) return

    const width_val = containerRef.value.offsetWidth
    containerWidth.value = width_val

    let columns = Math.floor(
        (width_val + config.gap) / (config.baseColumnWidth + config.gap)
    )
    columns = Math.max(columns, config.minColumns)

    let width = (width_val - (columns - 1) * config.gap) / columns
    // 允许在移动端稍微缩小一点以适应屏幕，同时限制最大宽度
    width = Math.max(
        config.minColumnWidth,
        Math.min(config.maxColumnWidth, width)
    )

    columnCount.value = columns
    columnWidth.value = Math.floor(width) // 取整避免亚像素偏移
    isLayoutReady.value = true
}

// ========== 数据相关 ==========
// 模板列表数据
const items = ref([])

// 轮播置顶列表数据
const carouselItems = ref([])

// 加载置顶列表（不缓存，运营数据需实时更新）
const loadTopList = async () => {
    try {
        const res = await getEffectTemplateTopApi({
            type: 'VIDEO',
            lang: locale.value,
        })
        if (res.status === 200 && res.data) {
            carouselItems.value = res.data.map(item => ({
                video: item.video_urls?.[0] || '',
                cover: item.cover_url,
                mask: '',
                desc: item.name,
                slug:
                    item.path_url ||
                    item.name?.toLowerCase().replace(/\s+/g, '-'),
            }))
        }
    } catch (error) {
        console.error('加载置顶列表失败:', error)
    }
}

// 清除所有分页缓存
const clearAllPageCache = lang => {
    // 清除所有分页缓存（假设最多缓存 50 页）
    for (let i = 1; i <= 50; i++) {
        storage.remove(CACHE_KEYS.VIDEO_LIST(lang, i))
    }
}

// 加载视频特效模板列表（使用后端时间戳判断是否需要刷新缓存）
const loadTemplates = async (page = 1, append = false) => {
    if (loading.value) return
    loading.value = true

    // 非追加模式时，先隐藏布局
    if (!append) {
        isLayoutReady.value = false
    }

    try {
        const cacheKey = CACHE_KEYS.VIDEO_LIST(locale.value, page)
        const timestampKey = CACHE_KEYS.VIDEO_TIMESTAMP(locale.value)

        // 1. 先请求时间戳接口，获取后端数据最后更新时间
        let serverTimestamp = 0
        try {
            const timestampRes = await getEffectTemplateLastUpdateApi('VIDEO')
            serverTimestamp = timestampRes.data || 0
        } catch (err) {
            console.warn('获取时间戳失败，将重新请求数据:', err)
        }

        // 2. 获取本地缓存的时间戳
        const localTimestamp = storage.get(timestampKey) || 0

        // 3. 判断是否可以使用缓存：后端时间戳有效(>0) 且 等于本地时间戳
        const canUseCache =
            serverTimestamp > 0 && serverTimestamp === localTimestamp

        // 4. 如果不能使用缓存且本地有旧缓存，清除所有分页缓存
        if (!canUseCache && localTimestamp > 0) {
            console.log('[video-effects] 时间戳变化或无效，清除所有分页缓存')
            clearAllPageCache(locale.value)
        }

        // 更新本地时间戳（只在后端时间戳有效且与本地不一致时更新）
        if (serverTimestamp > 0 && serverTimestamp !== localTimestamp) {
            storage.set(timestampKey, serverTimestamp)
        }

        // 5. 获取缓存数据
        const cached = storage.get(cacheKey)

        // 6. 如果可以使用缓存且有缓存数据，直接使用
        if (canUseCache && cached) {
            console.log('[video-effects] 时间戳一致，使用缓存')
            if (append) {
                items.value = [...items.value, ...cached.items]
            } else {
                items.value = cached.items
            }
            hasMore.value = cached.hasMore
            currentPage.value = page
            loading.value = false
            nextTick(() => {
                isLayoutReady.value = true
            })
            return
        }

        // 7. 其他情况（时间戳为0/null或不一致或无缓存）：请求接口
        console.log('[video-effects] 请求数据，page:', page)
        const res = await getEffectTemplatePageApi({
            type: 'VIDEO',
            pageNum: page,
            pageSize: pageSize,
            lang: locale.value,
        })

        if (res.status === 200 && res.data?.content) {
            // 并行预加载所有封面图片，获取实际尺寸
            const newItems = await Promise.all(
                res.data.content.map(async item => {
                    const size = await preloadImage(item.cover_url)
                    const ratioFromApi = parseAspectRatio(item.aspect_ratio)
                    const ratioFromImage =
                        size && size.width > 0 && size.height > 0
                            ? size.width / size.height
                            : null
                    return {
                        id: String(item.id),
                        slug:
                            item.path_url ||
                            item.name?.toLowerCase().replace(/\s+/g, '-'),
                        cover: item.cover_url,
                        video: item.video_urls?.[0] || '',
                        title: item.name,
                        likes: 0,
                        // 优先使用后端宽高比，预加载失败时不再误用默认值
                        aspectRatio:
                            ratioFromApi ||
                            ratioFromImage ||
                            config.defaultAspectRatio,
                        aspect_ratio: item.aspect_ratio,
                        type: item.type,
                        tags: item.tags,
                    }
                })
            )

            if (append) {
                items.value = [...items.value, ...newItems]
            } else {
                items.value = newItems
            }

            const hasMoreData = newItems.length >= pageSize
            hasMore.value = hasMoreData
            currentPage.value = page

            // 8. 更新缓存（不设过期时间，由时间戳判断是否刷新）
            storage.set(cacheKey, { items: newItems, hasMore: hasMoreData })
        } else {
            if (!append) {
                items.value = []
            }
            hasMore.value = false
        }
    } catch (error) {
        console.error('加载视频特效模板失败:', error)
        hasMore.value = false
    } finally {
        loading.value = false
        // 预加载完成，显示布局
        nextTick(() => {
            isLayoutReady.value = true
            // 检查是否需要继续加载更多（大屏幕场景）
            checkAndLoadMore()
        })
    }
}

// 加载更多
const loadMore = () => {
    if (loading.value || !hasMore.value) return
    loadTemplates(currentPage.value + 1, true)
}

// IntersectionObserver 用于无限滚动加载（不阻塞主线程）
let loadMoreObserver = null
const loadMoreSentinel = ref(null)

// 检查是否需要自动加载更多（大屏幕场景：内容未填满视口时自动加载）
const checkAndLoadMore = () => {
    if (loading.value || !hasMore.value || !loadMoreSentinel.value) return

    const rect = loadMoreSentinel.value.getBoundingClientRect()
    // 如果 sentinel 在视口内（加 200px margin），继续加载
    if (rect.top < window.innerHeight + 200) {
        loadMore()
    }
}

// ========== 网格布局计算（瀑布流，高度自适应） ==========
const layoutInfo = computed(() => {
    if (containerWidth.value <= 0) return { offsetX: 0, totalWidth: 0 }
    const totalWidth =
        columnCount.value * columnWidth.value +
        (columnCount.value - 1) * config.gap
    const offsetX = Math.floor(
        Math.max(0, (containerWidth.value - totalWidth) / 2)
    )
    return { offsetX, totalWidth }
})

const gridItems = computed(() => {
    if (!containerRef.value) return []

    const { offsetX } = layoutInfo.value
    const heights = new Array(columnCount.value).fill(0)

    return items.value.map(item => {
        const colIndex = heights.indexOf(Math.min(...heights))
        const height = calculateItemHeight(item)

        const left = colIndex * (columnWidth.value + config.gap) + offsetX
        const top = heights[colIndex]

        heights[colIndex] += height + config.gap

        return {
            ...item,
            calculatedHeight: height,
            style: {
                left: `${left}px`,
                top: `${top}px`,
            },
        }
    })
})

// 容器高度
const containerHeight = computed(() => {
    if (gridItems.value.length === 0) return 400

    const heights = new Array(columnCount.value).fill(0)
    items.value.forEach(item => {
        const colIndex = heights.indexOf(Math.min(...heights))
        heights[colIndex] += calculateItemHeight(item) + config.gap
    })

    return Math.max(...heights, 400)
})

// ========== 事件处理 ==========
// 点击卡片
const handleCardClick = item => {
    handleUseTemplate(item)
}

// 处理使用模板
const handleUseTemplate = item => {
    console.log('使用模板:', item)
    const slug =
        item.slug || item.title?.toLowerCase().replace(/\s+/g, '-') || item.id
    // 关键：直接跳到带语言前缀的地址，避免全局中间件 replace 导致历史栈异常
    navigateTo(withUrlPrefix(`/video-effects/${slug}`))
}

// ========== 暂时注释掉的分类相关代码，数据多了再启用 ==========
/*
// 当前激活的 Tab
const activeTab = ref('')

// 吸顶 Tab 显示状态
const showStickyTabs = ref(false)
const stickyThreshold = 450

// 滚动监听（分类版）
const handleScrollForCategories = () => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    showStickyTabs.value = scrollTop > stickyThreshold
    
    const headerOffset = 150
    for (let i = categoryTabs.value.length - 1; i >= 0; i--) {
        const tab = categoryTabs.value[i]
        const el = document.getElementById(`category-${tab.id}`)
        if (el) {
            const rect = el.getBoundingClientRect()
            if (rect.top <= headerOffset) {
                activeTab.value = tab.id
                break
            }
        }
    }
}

// Tab 列表（从接口获取）
const categoryTabs = ref([])

// 各分类的模板数据
const categoryTemplates = ref({})

// 加载标签列表
const loadTagList = async () => {
    try {
        const res = await getEffectTemplateTagListApi(locale.value, 'VIDEO')
        if (res.status === 200 && res.data) {
            categoryTabs.value = res.data
                .filter(item => !item.status || item.status === 'ENABLE')
                .map(item => ({
                    id: item.tag || String(item.id),
                    tagId: item.id,
                    name: item.name,
                    icon: '',
                }))
            
            if (categoryTabs.value.length > 0) {
                activeTab.value = categoryTabs.value[0].id
            }
            
            await loadTemplatesForCategories()
        }
    } catch (error) {
        console.error('加载标签列表失败:', error)
    }
}

// 加载各分类的模板数据
const loadTemplatesForCategories = async () => {
    try {
        const promises = categoryTabs.value.map(async (tab) => {
            const res = await getEffectTemplatePageApi({
                type: 'VIDEO',
                tagId: tab.tagId,
                pageNum: 1,
                pageSize: 12,
                lang: locale.value
            })
            
            if (res.status === 200 && res.data?.content) {
                const firstItem = res.data.content[0]
                if (firstItem?.tags?.length > 0) {
                    const englishTag = firstItem.tags[0]
                    tab.id = englishTag.toLowerCase().replace(/\s+/g, '-')
                }
                
                categoryTemplates.value[tab.id] = res.data.content.map(item => ({
                    id: String(item.id),
                    slug: item.path_url || item.name?.toLowerCase().replace(/\s+/g, '-'),
                    cover: item.cover_url,
                    video: item.video_urls?.[0] || '',
                    title: item.name,
                    likes: 0,
                    aspect_ratio: '9:16',
                    type: item.type,
                    tags: item.tags
                }))
            }
        })
        
        await Promise.all(promises)
    } catch (error) {
        console.error('加载模板列表失败:', error)
    }
}

// 各分类数据
const categoriesData = computed(() => {
    return categoryTabs.value.map(tab => ({
        ...tab,
        items: categoryTemplates.value[tab.id] || []
    }))
})

// 根据当前 Tab 显示的分类
const visibleCategories = computed(() => {
    return categoriesData.value
})

// Tab 切换
const handleTabChange = (tabId) => {
    console.log('Tab changed:', tabId)
    const el = document.getElementById(`category-${tabId}`)
    if (el) {
        const headerOffset = 120
        const elementPosition = el.getBoundingClientRect().top
        const offsetPosition = elementPosition + window.scrollY - headerOffset
        
        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        })
    }
}
*/

// ========== 生命周期 ==========
let resizeObserver = null

onMounted(() => {
    isClient.value = true
    analyticsEvent('page_view_video_effects')

    calculateColumns() // 首次即时计算，确保 layout 状态尽快 ready

    if (containerRef.value) {
        resizeObserver = new ResizeObserver(() => {
            calculateColumns()
        })
        resizeObserver.observe(containerRef.value)
    }

    // 加载置顶列表
    loadTopList()

    // 加载模板数据（简化版）
    loadTemplates()

    // 使用 IntersectionObserver 监听加载更多（不阻塞主线程）
    nextTick(() => {
        if (loadMoreSentinel.value) {
            loadMoreObserver = new IntersectionObserver(
                entries => {
                    if (
                        entries[0].isIntersecting &&
                        !loading.value &&
                        hasMore.value
                    ) {
                        loadMore()
                    }
                },
                { rootMargin: '200px' }
            )

            loadMoreObserver.observe(loadMoreSentinel.value)
        }
    })
})

onUnmounted(() => {
    if (resizeObserver) {
        resizeObserver.disconnect()
    }

    if (loadMoreObserver) {
        loadMoreObserver.disconnect()
    }
})

// 监听数据变化
watch(
    () => items.value,
    () => {
        nextTick(() => {
            calculateColumns()
        })
    },
    { deep: true }
)
</script>

<style lang="less" scoped>
@import '@/assets/css/mixins.less';

.video-effects-page {
    min-height: calc(100vh - 52px);
    width: 100%;
    padding: 0;
    padding-bottom: 30px;
}

.section-title {
    color: #fff;
    font-family: Roboto;
    font-size: 24px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    margin-bottom: 20px;
}

.video-effects-grid {
    position: relative;
    min-height: 400px;
    opacity: 0;

    &.is-ready {
        opacity: 1;
        transition:
            opacity 0.2s ease,
            height 0.3s ease;
    }
}

.grid-item {
    transition:
        left 0.3s ease,
        top 0.3s ease;
}

// ========== 暂时保留的样式，数据多了再启用 ==========
.tools-container {
    max-width: none;
    margin: 0;
}

.tool-category {
    margin-bottom: 40px;

    .category-header {
        display: flex;
        align-items: center;
        margin-bottom: 20px;

        .category-title {
            font-size: 24px;
            font-weight: 500;
            color: #ffffff;
            margin-left: 10px;
        }
    }
}

.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
}

.video-effect-card {
    &:hover {
        .bg-gradient-to-t {
            background: linear-gradient(to top,
                    rgba(0, 0, 0, 0.9),
                    transparent);
        }
    }
}

.noise-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding-left: 20px;
    padding-bottom: 20px;
    padding-top: 20px;
    padding-right: 20px;

    .tool-content {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: flex-start;
        height: 100%;
        width: 80%;

        .tool-title {
            color: #fff;
            text-shadow: 0 2px 1px rgba(0, 0, 0, 0.25);
            font-family: Roboto, sans-serif;
            font-size: 18px;
            font-style: normal;
            font-weight: 500;
            line-height: normal;
        }

        .tool-desc {
            color: #fff;
            text-shadow: 0 2px 1px rgba(0, 0, 0, 0.25);
            font-family: Roboto, sans-serif;
            font-size: 16px;
            font-style: normal;
            font-weight: 400;
            text-align: left;
            line-height: normal;
        }
    }
}

// 响应式设计
@media (max-width: 768px) {
    .tools-container {
        padding: 15px;
    }

    .tool-category {
        .category-title {
            font-size: 20px;
        }
    }

    .noise-bg {
        padding: 15px;

        .tool-content {
            .tool-title {
                font-size: 16px;
            }

            .tool-desc {
                font-size: 13px;
            }
        }
    }
}
</style>
